/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 **/
import { useState, FormEvent, KeyboardEvent, MouseEvent } from 'react'
import { Button, Icon, Text, TextField, View } from '@az/starc-ui'

import { Search } from '@az/starc-ui-icons'
import { useTranslation } from 'react-i18next'
import { padTo8LeadingZeroes, stripLeadingZeroes, setInputIfDigits } from '../../util'

import style from './StoreInput.module.css'

interface StoreInputProps {
  showErrorNotification: () => void
  setStore: (store: string) => void
}

const StoreInput: React.FC<StoreInputProps> = props => {
  const MAX_STORE_NUMBER_LENGTH = 8
  const { showErrorNotification, setStore } = props
  const [storeInputValue, setStoreInputValue] = useState<string>('')
  const [firstStoreInputValue, setFirstStoreInputValue] = useState<string>('')
  const { t } = useTranslation()

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    processStoreInput()
  }

  const handleButtonClick = (event: KeyboardEvent<HTMLElement> | MouseEvent<HTMLElement>) => {
    event.preventDefault()
    processStoreInput()
  }

  const processStoreInput = () => {
    if (firstStoreInputValue && firstStoreInputValue.length > 0) {
      if (stripLeadingZeroes(firstStoreInputValue) === stripLeadingZeroes(storeInputValue)) {
        setFirstStoreInputValue('')
        setStore(padTo8LeadingZeroes(firstStoreInputValue))
      } else {
        setStoreInputValue('')
        setFirstStoreInputValue('')
        showErrorNotification()
      }
    } else {
      setFirstStoreInputValue(storeInputValue)
      setStoreInputValue('')
    }
  }

  return (
    <form onSubmit={handleFormSubmit}>
      <View
        padding={[8, 5]}
        align="center"
        attributes={{ style: { paddingBottom: '20px' } }}
        direction="row"
        justify="center"
      >
        <View.Item className={style.storeInputContainer}>
          <TextField
            id="search-input"
            className={style.storeInput}
            name="store_number"
            label={''}
            value={storeInputValue}
            size="sm"
            inputAttributes={{
              placeholder: firstStoreInputValue ? t('re-enter') : t('search-store')
            }}
            onValueChange={value => {
              if (value.length <= MAX_STORE_NUMBER_LENGTH)
                setInputIfDigits({ inputSetter: setStoreInputValue, value })
            }}
            startElement={
              <Icon
                svg={Search}
                color="500"
                attributes={{ style: { marginRight: 'var(--st-unit-3)' } }}
              />
            }
          />
        </View.Item>
        <View.Item>
          <Button
            attributes={{ id: 'search-btn' }}
            className={style.storeButton}
            disabled={storeInputValue === '' || Number(storeInputValue) === 0}
            onClick={handleButtonClick}
          >
            <Text align="center" className={style.storeButtonText}>
              {t('search')}
            </Text>
          </Button>
        </View.Item>
      </View>
    </form>
  )
}
export default StoreInput
